import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Clock, BookOpen, Users, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { buildCourseListSchema } from '../data/courseListSchemaBuilder';

const Courses = () => {
  const courseListSchema = buildCourseListSchema();
  return (
    <div className="pt-16 md:pt-20">
      <SEO
        title="Our Courses — MS Office, Tally, Python, Web Development, Photoshop & More"
        description="Explore all computer courses at Genius Academy Kalaburagi: MS Office Course, Tally with GST, Basic Computer Course, Typing Classes, Python, Web Development, Photoshop, CorelDRAW, Computer Hardware and AI Tools — with 1:1 accountability."
        path="/courses"
        keywords="MS Office Course, Tally Course, Basic Computer Course, Typing Classes, Python Course, Web Development Course, Photoshop Course, CorelDRAW Course, Computer Hardware Course, Computer Classes in Kalaburagi"
        jsonLd={courseListSchema}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Courses', path: '/courses' }]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-cyan-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6" data-testid="courses-page-title">
              Our <span className="text-cyan-600">Courses</span>
            </h1>
            <p className="text-base md:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              From basic computer skills to advanced programming and AI tools, we offer comprehensive training 
              tailored to your career goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Course Categories */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Computer Basics */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Computer Basics</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <CourseCard
                title="Fundamentals of Computer & Typing"
                duration="2-3 months"
                level="Beginner"
                topics={['Computer Basics', 'Keyboard & Mouse', 'Typing Speed', 'File Management']}
                href="/courses/typing"
              />
              <CourseCard
                title="Internet & Email"
                duration="1 month"
                level="Beginner"
                topics={['Web Browsing', 'Email Setup', 'Search Techniques', 'Online Safety']}
              />
            </div>
          </div>

          {/* MS Office Suite */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">MS Office Suite</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <CourseCard
                title="MS Word"
                duration="1.5 months"
                level="Beginner to Intermediate"
                topics={['Document Formatting', 'Tables', 'Mail Merge', 'Professional Reports']}
                href="/courses/ms-office"
              />
              <CourseCard
                title="MS Excel"
                duration="2 months"
                level="Beginner to Intermediate"
                topics={['Formulas & Functions', 'Data Analysis', 'Charts & Graphs', 'Pivot Tables']}
                href="/courses/ms-office"
              />
              <CourseCard
                title="MS PowerPoint"
                duration="1 month"
                level="Beginner to Intermediate"
                topics={['Slide Design', 'Animations', 'Presentations', 'Templates']}
                href="/courses/ms-office"
              />
            </div>
          </div>

          {/* Accounting */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Accounting & Finance</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <CourseCard
                title="Tally with GST"
                duration="3 months"
                level="Beginner to Advanced"
                topics={['Ledger Accounts', 'Trial Balance', 'Balance Sheet', 'GST Filing', 'Income Tax', 'Reports']}
                href="/courses/tally-gst"
              />
            </div>
          </div>

          {/* Programming */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Programming</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <CourseCard
                title="Python"
                duration="3-4 months"
                level="Beginner to Intermediate"
                topics={['Python Basics', 'Loops & Functions', 'OOP Concepts', 'Mini Projects']}
                href="/courses/python"
              />
              <CourseCard
                title="C & C++"
                duration="3-4 months"
                level="Beginner to Intermediate"
                topics={['Basics of C', 'Data Types', 'Functions', 'OOP in C++', 'Problem Solving']}
              />
              <CourseCard
                title="Basic Java"
                duration="3 months"
                level="Beginner"
                topics={['Java Fundamentals', 'OOP Concepts', 'Collections', 'Basic Projects']}
              />
            </div>
          </div>

          {/* Design & Multimedia */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Design & Multimedia</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <CourseCard
                title="Photoshop"
                duration="2 months"
                level="Beginner to Intermediate"
                topics={['Photo Editing', 'Layers', 'Filters', 'Design Projects']}
                href="/courses/photoshop"
              />
              <CourseCard
                title="CorelDraw"
                duration="2 months"
                level="Beginner to Intermediate"
                topics={['Vector Graphics', 'Logo Design', 'Brochures', 'Print Design']}
              />
              <CourseCard
                title="PageMaker"
                duration="1.5 months"
                level="Beginner"
                topics={['Desktop Publishing', 'Page Layout', 'Typography', 'Print Materials']}
              />
            </div>
          </div>

          {/* Hardware & Networking */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Hardware & Networking</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <CourseCard
                title="Computer Hardware"
                duration="3 months"
                level="Beginner to Advanced"
                topics={['Assembly & Disassembly', 'BIOS & OS Install', 'Troubleshooting', 'Basic Networking']}
                href="/courses/computer-hardware"
              />
            </div>
          </div>

          {/* AI & Modern Tools */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">AI & Modern Tools</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <CourseCard
                title="AI Tools Training"
                duration="1-2 months"
                level="All Levels"
                topics={['ChatGPT', 'Gemini', 'Claude', 'AI for Work', 'Prompt Engineering']}
              />
            </div>
          </div>

          {/* Regional Software */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Regional Software</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <CourseCard
                title="Kannada Software (Nudi)"
                duration="1 month"
                level="Beginner"
                topics={['Nudi Typing', 'Kannada Documents', 'Government Forms']}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Courses */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              Why Choose Our Courses?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 shadow-sm text-center"
            >
              <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-cyan-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Small Batches</h3>
              <p className="text-slate-600">
                Limited students per batch ensures you get personal attention and hands-on practice.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm text-center"
            >
              <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-cyan-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">100% Practical</h3>
              <p className="text-slate-600">
                Theory is important, but practice makes perfect. Every course is hands-on.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-sm text-center"
            >
              <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-cyan-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Flexible Timings</h3>
              <p className="text-slate-600">
                Morning, afternoon, or evening batches. We work around your schedule.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to Master a New Skill?
            </h2>
            <p className="text-lg md:text-xl mb-8 opacity-90">
              Book a free demo class and see how we teach. No obligations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 hover:bg-slate-50 rounded-full px-8 py-4 font-bold shadow-lg transition-all"
                data-testid="courses-cta-contact"
              >
                Book Free Demo
              </Link>
              <a
                href="https://wa.me/919164998997"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-4 font-bold transition-all"
                data-testid="courses-cta-whatsapp"
              >
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const CourseCard = ({ title, duration, level, topics, href }) => {
  const linkTo = href || '/contact';
  const ctaLabel = href ? 'View Course Details' : 'Enroll Now';
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-cyan-200 transition-all duration-300 p-6 hover:-translate-y-1"
    >
      <h3 className="text-xl font-bold text-slate-900 mb-4">{title}</h3>
      
      <div className="flex items-center gap-4 mb-4 text-sm text-slate-600">
        <div className="flex items-center gap-1">
          <Clock size={16} />
          <span>{duration}</span>
        </div>
        <div className="flex items-center gap-1">
          <BookOpen size={16} />
          <span>{level}</span>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-sm font-semibold text-slate-700 mb-2">What You'll Learn:</p>
        <ul className="space-y-1">
          {topics.map((topic, topicIndex) => (
            <li key={topicIndex} className="text-sm text-slate-600 flex items-start gap-2">
              <span className="text-cyan-600 mt-0.5">•</span>
              <span>{topic}</span>
            </li>
          ))}
        </ul>
      </div>

      <Link
        to={linkTo}
        className="inline-flex items-center gap-2 text-cyan-600 font-medium hover:text-cyan-700 transition-colors"
      >
        {ctaLabel} <ArrowRight size={16} />
      </Link>
    </motion.div>
  );
};

export default Courses;
